import React, { useState } from 'react';
import { Wrench, Eye, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const call = async (apply, token) => {
  const res = await fetch(`${API}/api/ka/admin/repair-searchinc?apply=${apply}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
  return data;
};

export const AdminAtlasRepair = () => {
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [msg, setMsg] = useState('');
  const authToken = localStorage.getItem('startech_token');

  const run = async (apply) => {
    setBusy(true); setMsg('');
    try {
      const data = await call(apply, authToken);
      setReport(data);
      setMsg(apply
        ? `Repair done. Stamped ${data.atlas_entities_stamped} entities and ${data.documents_stamped} documents. Reload the Atlas to see the Topic.`
        : `Preview only. ${data.atlas_entities_needing_stamp} entities and ${data.documents_needing_stamp} documents would be stamped.`);
    } catch (e) {
      setMsg(`Refused: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-slate-900/70 border border-slate-800 rounded-xl p-5 backdrop-blur-sm mb-8"
         data-testid="admin-atlas-repair-panel">
      <div className="flex items-center gap-2 mb-3">
        <Wrench size={18} className="text-cyan-400" />
        <h3 className="text-lg font-semibold text-cyan-300">SearchInc Topic Repair (IJ-497)</h3>
      </div>
      <p className="text-sm text-slate-400 mb-4">
        Stamps your account onto the SearchInc topic data so SearchInc_TheRevolution
        returns to the Topic dropdown. It only touches the SearchInc partition, never
        the Barbados topics, and it is safe to run more than once. Preview first, then run.
      </p>

      <div className="flex flex-wrap gap-3">
        <button disabled={busy} onClick={() => run(false)}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-sm text-white disabled:opacity-50"
          data-testid="admin-atlas-repair-preview-btn">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Eye size={15} />} Preview
        </button>
        <button disabled={busy} onClick={() => run(true)}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-cyan-700 hover:bg-cyan-600 text-sm text-white disabled:opacity-50"
          data-testid="admin-atlas-repair-run-btn">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Wrench size={15} />} Run repair
        </button>
      </div>

      {msg && (
        <div className="mt-4 text-xs text-slate-300 bg-slate-950/80 border border-slate-800 rounded-lg p-3 break-all"
             data-testid="admin-atlas-repair-message">
          {msg}
        </div>
      )}

      {report && (
        <div className="mt-3 text-[11px] text-slate-400 grid grid-cols-2 gap-x-4 gap-y-1"
             data-testid="admin-atlas-repair-report">
          <span>Partition</span><span className="text-slate-200">{report.partition}</span>
          <span>SearchInc entities total</span><span className="text-slate-200">{report.atlas_entities_in_partition_total}</span>
          <span>Entities needing stamp</span><span className="text-slate-200">{report.atlas_entities_needing_stamp}</span>
          <span>Documents needing stamp</span><span className="text-slate-200">{report.documents_needing_stamp}</span>
          <span>Other partitions untouched</span><span className="text-slate-200">{report.other_partition_atlas_entities_untouched}</span>
        </div>
      )}
    </div>
  );
};

export default AdminAtlasRepair;
